import React, { useState, useEffect, useRef } from "react";
import "./Dashboard.css";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://www.lianfoong.com";

/** Get server time once (UTC) via Date header, fallback to /api/system-date if you have it */
async function fetchServerNowUtc() {
  // Prefer a cheap GET to your own origin; Date header is present & readable (same-origin).
  try {
    const res = await fetch(`${window.location.origin}/`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h); // GMT
  } catch {}

  // Fallback: favicon is tiny and also returns a Date header
  try {
    const res = await fetch(`${window.location.origin}/favicon.ico`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}

  // Last resort: local clock (still works, just not immune to a bad PC clock)
  return new Date();
}


/** Convert an instant to a display string in Asia/Singapore */
function fmtDateSg(d) {
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Singapore" });
}
function fmtTimeSg(d) {
  return d.toLocaleTimeString("en-GB", { timeZone: "Asia/Singapore" });
}

/** Pawn expiry calculation using Singapore calendar components only. */
const SG_OFFSET_MS = 8 * 60 * 60 * 1000;

function computeExpiryFrom(utcNow) {
  // Shift the instant to a pseudo-UTC value whose UTC fields equal the SGT fields.
  // Using UTC getters here avoids depending on the pawnshop PC's configured timezone.
  const sg = new Date(utcNow.getTime() + SG_OFFSET_MS);
  const year = sg.getUTCFullYear();
  const month = sg.getUTCMonth();
  const day = sg.getUTCDate();

  const targetMonthIndex = month + 6;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth + 1, 0)
  ).getUTCDate();

  let expiryPseudoUtc;
  if (day > lastDayOfTargetMonth) {
    // 29/30/31 Aug 2026 -> 28 Feb 2027 (not 27 Feb / 1 Mar / 2 Mar).
    expiryPseudoUtc = new Date(
      Date.UTC(targetYear, targetMonth, lastDayOfTargetMonth)
    );
  } else {
    expiryPseudoUtc = new Date(Date.UTC(targetYear, targetMonth, day));
    expiryPseudoUtc.setUTCDate(expiryPseudoUtc.getUTCDate() - 1);
  }

  // Convert the pseudo SGT-midnight value back to a real instant.
  return new Date(expiryPseudoUtc.getTime() - SG_OFFSET_MS);
}

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // we keep a fixed offset that corrects whatever the PC clock is
  const offsetRef = useRef(0); // serverNowUTC - clientNowUTC
  const [now, setNow] = useState(new Date());        // corrected "now" (instant)
  const [expiryUtc, setExpiryUtc] = useState(new Date()); // expiry computed from corrected SGT

  // Get server time & start ticking using the corrected offset
  useEffect(() => {
    let tickId;
    (async () => {
      const serverNow = await fetchServerNowUtc();
      const clientNow = new Date();
      offsetRef.current = serverNow.getTime() - clientNow.getTime();

      const update = () => {
        const corrected = new Date(Date.now() + offsetRef.current);
        setNow(corrected);
        setExpiryUtc(computeExpiryFrom(corrected));
      };

      update(); // initial
      tickId = setInterval(update, 1000); // 1s clock tick
    })();

    return () => clearInterval(tickId);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    // IMPORTANT: stop honoring any leftover customDate on clients
    localStorage.removeItem("customDate");
    navigate("/");
  };

  const handleReprint = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/pawn-transactions/reprint`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to reprint. Server responded with ${response.status}: ${errorText}`);
      }

      let filename = "receipt.pdf";
      const disposition = response.headers.get("Content-Disposition");
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error during reprint:", error);
      alert(`Error: ${error.message}`);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      const key = e.key.toLowerCase();
      switch (key) {
        case "1": navigate("/grant"); break;
        case "2": navigate("/redeem"); break;
        case "f": navigate("/fast-renew"); break;
        case "r": navigate("/renew"); break;
        case "6": handleReprint(); break;
        case "3": navigate("/enquiry"); break;
        case "4": navigate("/adjust"); break;
        case "5": navigate("/print-report"); break;
        case "o": navigate("/other"); break;
        case "n": navigate("/add-customer"); break;
        case "c": navigate("/change-gold-value"); break;
        case "p": navigate("/print-receipt"); break;
        case "a": navigate("/adjust-payment"); break;
        case "t": navigate("/terror-import"); break;
        case "7": navigate("/day-summary"); break;
        case "8": navigate("/renewal-summary"); break;
        case "d": navigate("/change-date"); break;
        case "e": navigate("/expired"); break;
        default: break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [navigate]);

  return (
    <div className="dashboard">
      <button className="logout-button" onClick={handleLogout}>Logout</button>
      <h1>LIAN FOONG PAWNSHOP</h1>
      <h2>Lian Foong Pawnshop Pte Ltd</h2>

      <div className="date-time-container">
        {/* All derived from corrected "now" */}
        <div className="date-box">DATE: {fmtDateSg(now)}</div>
        <div className="expiry-box">EXPIRY: {fmtDateSg(expiryUtc)}</div>
        <div className="time-box">TIME: {fmtTimeSg(now)}</div>
      </div>

      <div className="dashboard-container">
        <div className="column">
          <button onClick={() => navigate("/grant")}>[1] Grant</button>
          <button onClick={() => navigate("/redeem")}>[2] Redeem</button>
          <button onClick={() => navigate("/fast-renew")}>[F] Fast Renew</button>
          <button onClick={() => navigate("/renew")}>[R] Renew</button>
          <button onClick={handleReprint}>[6] Reprint</button>
        </div>
        <div className="column">
          <button onClick={() => navigate("/enquiry")}>[3] Enquiry</button>
          <button onClick={() => navigate("/adjust")}>[4] Adjust</button>
          <button onClick={() => navigate("/print-report")}>[5] Print Report</button>
          <button onClick={() => navigate("/change-date")}>[D] Change Date</button>
          <button onClick={() => navigate("/other")}>[O] Other</button>
        </div>
        <div className="column">
          <button onClick={() => navigate("/add-customer")}>[N] Add Pawner</button>
          <button onClick={() => navigate("/change-gold-value")}>[C]Change Gold Value</button>
          <button onClick={() => navigate("/print-receipt")}>[P] Print Receipt</button>
          <button onClick={() => navigate("/adjust-payment")}>[A] Adjust Payment</button>
          <button onClick={() => navigate("/terror-import")}>[T] Import Terrorist List</button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
